// Recipe cook-stats — viewer-scoped aggregate of cook_logs by
// recipe_slug. Powers the "cooked Nx · last Y" badges on the
// Recipe Book TOC and recipe pages, plus the MOST COOKED / LAST
// COOKED sort options.
//
// Why a dedicated hook (vs. reusing useCookLog): useCookLog returns
// the full cook_logs row set; we want a Map<slug, stats> shape.
// Aggregating on every render of the Recipe Book would either
// duplicate work or require memoization gymnastics. Cleaner to
// own the aggregation here, with its own realtime subscription.
//
// Slug-collision guard: cook_logs.recipe_slug is denormalized text
// with no FK (it covers bundled + user recipes alike). Two users
// could both have a custom recipe with slug "pasta-primavera" —
// without filtering on user_id we'd silently pool their cook
// counts. The query filters on user_id = viewer, and the join
// at the consumer level (RecipeBook) is implicit since the book
// only renders the viewer's own user_recipes anyway.
//
// Realtime: any cook_logs INSERT/UPDATE/DELETE for this user
// triggers a refresh. Incremental aggregation on the realtime
// payload is fragile (DELETE events may not carry the original
// recipe_slug), so a refetch is the cheapest correct response.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

function aggregate(logs) {
  const map = new Map();
  for (const log of logs) {
    if (!log?.recipe_slug) continue;
    const slug = log.recipe_slug;
    const t = log.cooked_at ? +new Date(log.cooked_at) : 0;
    const cur = map.get(slug);
    if (!cur) {
      map.set(slug, {
        cookCount: 1,
        lastCookedAt: t || null,
        lastRating: log.rating || null,
      });
    } else {
      cur.cookCount += 1;
      if (t > (cur.lastCookedAt || 0)) {
        cur.lastCookedAt = t;
        cur.lastRating = log.rating || null;
      }
    }
  }
  return map;
}

export function useRecipeCookStats(userId) {
  const [stats, setStats] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const setStatsRef = useRef(setStats);
  useEffect(() => { setStatsRef.current = setStats; }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setStats(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("cook_logs")
      .select("recipe_slug, cooked_at, rating")
      .eq("user_id", userId)
      .order("cooked_at", { ascending: false });
    if (error) {
      // eslint-disable-next-line no-console
      console.error("[recipeCookStats] load failed:", error);
      setStats(new Map());
    } else {
      setStats(aggregate(data || []));
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:cookStats:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_logs", filter: `user_id=eq.${userId}` },
        () => { refresh(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, refresh]);

  return { stats, loading, refresh };
}

// Small util reused by the Recipe Book UI. "cooked 3 weeks ago"
// style relative formatting. Returns null for missing input.
export function relativeTimeShort(t) {
  if (!t) return null;
  const diff = Date.now() - t;
  if (diff < 0) return "soon";
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} ${w === 1 ? "week" : "weeks"} ago`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} ${m === 1 ? "month" : "months"} ago`;
  }
  const y = Math.floor(days / 365);
  return `${y} ${y === 1 ? "year" : "years"} ago`;
}
